"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { Button } from "@tillix/ui-atoms/button";
import { Dialog } from "@tillix/ui-atoms/dialog";
import { cn } from "@tillix/ui-atoms/lib/utils";
import { toastManager } from "@tillix/ui-atoms/toast";
import { cancelOrder } from "../actions";
import { useAppointmentT } from "../i18n/use-t";
import { isCompleteContact } from "../lib/contact";
import { dashboardNoticeHref } from "../lib/dashboard-notice";
import { errorKey } from "../lib/errors";
import { sessionExpiredHref } from "../lib/session-expired";
import { useSapFailureFlags } from "../lib/use-sap-failure-flags";
import ContactCapture from "./ContactCapture";
import type { ContactInput } from "../lib/contact";

interface CancelDialogProps {
  orderNumber: string;
  className?: string;
  /** "short" renders "Cancel" (dashboard rows); "long" renders "Cancel Appointment" (order page). */
  variant?: "short" | "long";
  /** Order capability token from a Nudge-UUID entry; cookie auth otherwise. */
  grant?: string;
  /** The SAP order has no contact info — collect one for the confirmation nudge. */
  needsContact?: boolean;
}

export default function CancelDialog({
  orderNumber,
  className,
  variant = "short",
  grant,
  needsContact = false,
}: CancelDialogProps) {
  const t = useAppointmentT();
  const router = useRouter();
  const sapFailures = useSapFailureFlags();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contact, setContact] = useState<ContactInput>({
    channel: "email",
    value: "",
  });

  const handleConfirm = async () => {
    setSubmitting(true);
    const result = await cancelOrder(
      orderNumber,
      grant,
      needsContact
        ? { channel: contact.channel, value: contact.value.trim() }
        : undefined,
      sapFailures,
    );
    if (result.ok) {
      setOpen(false);
      // The confirmation is a dashboard banner (approved copy), reached by
      // navigation from both the dashboard row and the order page. The
      // dashboard RSC is dynamic, so the push fetches fresh data — no
      // router.refresh(), which would supersede the pending navigation.
      router.push(dashboardNoticeHref("cancelled", orderNumber));
    } else if (result.error === "NOT_AUTHED") {
      // Session timed out mid-flow: back to the gate with the timeout dialog.
      router.replace(sessionExpiredHref());
    } else {
      toastManager.add({
        title: t(errorKey("cancel.errors", result.error)),
        type: "error",
      });
      setSubmitting(false);
    }
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <Dialog.Trigger
        render={(props) => (
          <Button
            {...props}
            className={cn(props.className, className)}
            size="sm"
            variant="destructive"
          >
            {variant === "long" ? t("cancel.triggerLong") : t("cancel.trigger")}
          </Button>
        )}
      />
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup>
          <Dialog.Header>
            <Dialog.Title>{t("cancel.title")}</Dialog.Title>
            {/* A <div>, not the default <p>, since it holds one <p> per line */}
            <Dialog.Description render={<div />}>
              {t.list("cancel.description").map((line, index) => (
                <p key={index.toString()}>{line}</p>
              ))}
            </Dialog.Description>
          </Dialog.Header>
          {needsContact && (
            <div className="mt-4">
              <ContactCapture contact={contact} onChange={setContact} />
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Dialog.Close
              render={(props) => (
                <Button {...props} variant="ghost">
                  {t("cancel.dismiss")}
                </Button>
              )}
            />
            <Button
              disabled={
                submitting || (needsContact && !isCompleteContact(contact))
              }
              onClick={handleConfirm}
              variant="destructive"
            >
              {t("cancel.confirm")}
            </Button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog>
  );
}
